import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDate } from "../../../../utils/Date";
import {
  numberFormatter,
  statusValueFormatter,
} from "../../../../utils/Formatter/Global";
import { dateFilterParams } from "../../../../utils/AgGridFilter";

import CustomizedStatus from "../../../Custom/CustomizedStatus";
import CustomizedAvatar from "../../../Custom/CustomizedAvatar";

import { usePurchaseAgGrid } from "../../../../hooks/Purchase/use-purchase-aggrid";

export const usePurchaseOrderColumnDefs = () => {
  const { t } = useTranslation();
  const [columnDef, setColumnDef] = useState<ColDef[]>();

  const { purchaseUserFilterParams } = usePurchaseAgGrid("order");

  useEffect(() => {
    setColumnDef([
      {
        field: "unique_id",
        headerName: t("sales.unique_id"),
        filter: "agTextColumnFilter",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "issue_date",
        headerName: t("date.issue_date"),
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "due_date",
        headerName: t("date.purchase_due_date"),
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "supplier_unique_id",
        headerName:
          t("purchase.supplier.unique_id") + t("purchase.supplier.index"),
        hide: true,
        filter: "agTextColumnFilter",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "supplier_name",
        headerName: t("purchase.supplier.name") + t("purchase.supplier.index"),
        filter: "agTextColumnFilter",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "net_amount",
        headerName: t("price.net_amount"),
        filter: "agNumberColumnFilter",
        valueFormatter: (params: ValueFormatterParams<any, any>) => {
          if (params.value) {
            return numberFormatter(params.value);
          } else return "";
        },
        minWidth: 200,
        flex: 1,
      },
      {
        field: "reference_no",
        headerName: t("purchase.reference_no"),
        hide: true,
        filter: "agTextColumnFilter",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "remark",
        headerName: t("sentence.remark_document"),
        filter: "agTextColumnFilter",
        hide: true,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "destination_warehouse_unique_id",
        headerName: t("warehouse.destination_unique_id"),
        filter: "agTextColumnFilter",
        hide: true,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "destination_warehouse_name",
        headerName: t("warehouse.destination"),
        filter: "agTextColumnFilter",
        hide: true,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "reference_document_list",
        headerName: t("purchase.reference_unique_id"),
        filter: "agTextColumnFilter",
        hide: true,
        minWidth: 200,
        flex: 1,
      },
      {
        field: "expected_date",
        headerName: t("date.purchase_expected_date"),
        filter: "agDateColumnFilter",
        hide: true,
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "created_date",
        headerName: t("date.created_date"),
        sort: "desc",
        hide: true,
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "last_updated_date",
        headerName: t("date.last_updated_date"),
        hide: true,
        filter: "agDateColumnFilter",
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "within_date",
        headerName: t("date.purchase_within_date"),
        filter: "agDateColumnFilter",
        hide: true,
        filterParams: dateFilterParams,
        minWidth: 200,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) =>
          formatDate(params.value),
      },
      {
        field: "created_by_object",
        headerName: t("sentence.created_by"),
        filter: "agSetColumnFilter",
        filterParams: purchaseUserFilterParams,
        cellRenderer: (params: ICellRendererParams<any, any>) => {
          if (params.value) {
            return <CustomizedAvatar avatars={[params.value]} />;
          }
        },
        minWidth: 200,
        flex: 1,
      },
      {
        field: "related_user_object_list",
        headerName: t("sales.employee_list"),
        filter: "agSetColumnFilter",
        filterParams: purchaseUserFilterParams,
        cellRenderer: (params: ICellRendererParams<any, any>) => {
          if (params.value && params.value.length > 0) {
            return <CustomizedAvatar avatars={params.value} />;
          }
        },
        minWidth: 200,
        flex: 1,
      },
      {
        field: "status",
        headerName: t("status.index"),
        filter: "agSetColumnFilter",
        cellRenderer: (params: ICellRendererParams<any, string>) => (
          <CustomizedStatus status={params.value} />
        ),
        cellStyle: {
          display: "flex",
          justifycontent: "center",
          alignItems: "center",
        },
        minWidth: 200,
        flex: 1,
        filterParams: {
          values: [
            "draft",
            "wait_ordered",
            "fully_ordered",
            "partially_imported",
            "fully_imported",
            "cancelled",
          ],
          valueFormatter: (params: ValueFormatterParams) =>
            statusValueFormatter(params.value),
        },
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  return columnDef;
};
